import { ApostleBase, ApostleSourceType } from '@apeiron/apostle'
import {
  ApeironLinkButton,
  MayNull,
  NextImage,
  noForwardProps,
  SkeletonBox
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ActiveForegroundPNG from '@public/images/background-apostle-active-card.png'
import InactiveForegroundPNG from '@public/images/background-apostle-inactive-card.png'
import ApostleOriginBorderPNG from '@public/images/image-apostle-origin-border.png'
import ApostleCardSkillList from '@src/components/apostles/card/ApostleCardSkillList'
import { GAAssetType } from '@src/constants/googleAnalytics'
import useTracking from '@src/hooks/common/analytics/useTracking'
import * as R from 'ramda'
import { ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

const Container = styled(SkeletonBox)`
  aspect-ratio: 320 / 504;
  position: relative;
  width: 160px;
`

const CardButton = styled(ApeironLinkButton, noForwardProps)<{
  __dimmed?: boolean
}>`
  height: 100%;
  position: relative;
  width: 100%;
  overflow: hidden;
  :disabled {
    opacity: 1;
    pointer-events: none;
  }

  ${props =>
    props.__dimmed
      ? `
      filter: brightness(0.5);
      opacity: 0.7;
    `
      : ''}
`

const ApostleImage = styled(NextImage, noForwardProps)<{
  __smallCard?: boolean
}>`
  aspect-ratio: 160 / 190;
  clip-path: polygon(26% 10%, 73% 10%, 100% 33%, 100% 100%, 0% 100%, 0% 33%);
  left: 50%;
  object-fit: cover;
  position: absolute;
  top: 43%;
  transform: translate(-50%, -50%);
  width: 135%;
  ${props => (props.__smallCard ? 'width: 110%;' : '')}
`

const ApostleIcon = styled(NextImage, noForwardProps)<SmallProps>`
  aspect-ratio: 1;
  left: 14%;
  position: absolute;
  top: 14%;
  transform: translate(-50%, -50%);
  width: 25%;
  ${props =>
    props.__smallCard
      ? ` 
        left: 12.5%;
        top: 12.5%;
      `
      : ''}
`

const IV = styled(Typography, noForwardProps)<SmallProps>`
  align-items: center;
  background-color: #1b2a61;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  display: flex;
  font-weight: 300;
  justify-content: center;
  font-size: 18px;
  line-height: 18px;
  position: absolute;
  top: 8.5%;
  width: 48%;
  height: 24px;
  ${props =>
    props.__smallCard
      ? ` 
        aspect-ratio: 100 / 28.5;
        font-size: 14px;
        line-height: 14px;
      `
      : ''}
`

const ID = styled(Typography, noForwardProps)<SmallProps>`
  color: #999999;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  top: 82%;
  font-size: 12px;
  line-height: 12px;
  font-weight: 300;
  ${props =>
    props.__smallCard
      ? ` 
        
      `
      : ''}
`

const OriginBorder = styled(NextImage)`
  position: absolute;
  width: 100%;
  height: 100%;
`

const Origin = styled(Typography, noForwardProps)<SmallProps>`
  background-color: #5b45ff;
  border-bottom-left-radius: 3px;
  position: absolute;
  ${props =>
    props.__smallCard
      ? ` 
        font-size: 10px;
        font-weight: 300;
        line-height: 10px;
        padding: 1px 4px;
        right: 3%;
        top: 74.5%;
      `
      : `
        font-size: 12px;
        font-weight: 300;
        line-height: 12px;
        padding: 3px 6px;
        right: 3%;
        top: 74%;
      `}
`

const NameBackground = styled(Box)`
  background-color: #1c1c21;
  border-radius: 0 0 43% 43%;
  bottom: 0;
  height: 18%;
  left: 50%;
  position: absolute;
  transform: translate(-50%);
  width: 100%;
`

const Name = styled(Typography, noForwardProps)<NameProps>`
  bottom: ${props => (props.__reserveTop ? '9%' : '11%')};
  color: white;
  left: 50%;
  position: absolute;
  transform: translate(-50%, 50%);
  ${props =>
    props.__smallCard
      ? ` 
        font-size: 18px;
        font-weight: 400;
      `
      : `
        font-size: 22px;
        font-weight: 400;
      `}
`

const Foregoround = styled(NextImage)`
  filter: drop-shadow(0px 0px 3px black);
  height: 100%;
  position: absolute;
  width: 100%;
`

const SkillList = styled(ApostleCardSkillList)`
  position: absolute;
  bottom: 21%;
  left: 4px;
`

const ApostleCard = <T extends ApostleBase>(props: ApostleCardProps<T>) => {
  const {
    apostle,
    className,
    enableLink = true,
    target = '_self',
    foregroundImage = ActiveForegroundPNG.src,
    loading = false,
    overlay,
    showId = true,
    showInfo = true,
    showName = true,
    skipTracking = false,
    smallCard = false,
    url,
    disabled = false,
    onClick,
    outerOverlay = null,
    dimmed = false
  } = props

  const { t } = useTranslation()

  const { gtmEmitViewItem } = useTracking()

  const { ref: inViewRef } = useInView({
    triggerOnce: true,
    onChange(inView) {
      if (inView && !skipTracking) {
        // GA-BMJskIwwJWbBwwr
        gtmEmitViewItem({
          type: GAAssetType.Apostle,
          item: apostle
        })
      }
    }
  })

  const handleOnClick = useCallback(() => {
    if (onClick) {
      onClick(apostle)
    }
  }, [apostle, onClick])

  return (
    <Container className={`${className || ''} Apostle-Card`} loading={loading}>
      <Box ref={inViewRef} />
      <CardButton
        className={'Custom-Button'}
        disabled={disabled}
        enableLink={enableLink}
        target={target}
        url={url}
        onClick={handleOnClick}
        aria-label={`apostle-card-${apostle?.id}`}
        __dimmed={dimmed}
      >
        {!R.isNil(apostle) ? (
          <>
            <ApostleImage
              className='Custom-Image'
              __smallCard={smallCard}
              src={apostle?.image}
            />
            <NameBackground />
            {apostle && showId && (
              <ID __smallCard={smallCard}>{`#${apostle.id}`}</ID>
            )}
            {apostle && showName && (
              <Name
                className='Custom-Name'
                __smallCard={smallCard}
                __reserveTop={showId}
              >
                {apostle?.class?.name}
              </Name>
            )}
            <Foregoround src={foregroundImage} />
            {apostle && showInfo && (
              <>
                {apostle.sourceType === ApostleSourceType.Origin && (
                  <>
                    <Origin className='Custom-Origin' __smallCard={smallCard}>
                      {t('apostle.origin')}
                    </Origin>
                    <OriginBorder src={ApostleOriginBorderPNG.src} />
                  </>
                )}
                <ApostleIcon
                  className='Custom-Icon'
                  __smallCard={smallCard}
                  src={apostle?.class?.icon}
                />
                <IV className='Custom-IV' __smallCard={smallCard}>
                  {t('apostle.iv', { value: apostle.iv })}
                </IV>
                <SkillList apostle={apostle} className='Custom-Skill' />
              </>
            )}
          </>
        ) : (
          <Foregoround
            className='Custom-Image'
            src={InactiveForegroundPNG.src}
          />
        )}
        {overlay}
      </CardButton>
      {outerOverlay}
    </Container>
  )
}

type SmallProps = {
  __smallCard?: boolean
}

type NameProps = SmallProps & {
  __reserveTop?: boolean
}

export type ApostleCardProps<T extends ApostleBase> = {
  apostle: MayNull<T>
  className?: string
  enableLink?: boolean
  target?: '_blank' | '_self' | '_parent' | '_top'
  foregroundImage?: string
  loading?: boolean
  overlay?: ReactNode
  showId?: boolean
  showInfo?: boolean
  showName?: boolean
  skipTracking?: boolean
  smallCard?: boolean
  url?: MayNull<string>
  disabled?: boolean
  onClick?: (apostle: MayNull<T>) => void
  outerOverlay?: ReactNode
  dimmed?: boolean
}

export default ApostleCard
